'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { useUserData } from '@/context/UserDataContext';
import { isProUser } from '@/lib/auth';
import { generateGardenTasks, type GardenTask } from '@/lib/gardenTasks';
import type { CanonicalPlant } from '@/lib/normalizePlant';
import { resolveCanonicalPlantsById } from '@/lib/plantCatalog';

const REMINDERS_ENABLED_KEY = 'garden-reminders-enabled';
const REMINDERS_SHOWN_KEY = 'garden-reminders-shown';
const REMINDERS_WEEKLY_KEY = 'garden-reminders-weekly';

type ShownReminderState = {
  date: string;
  taskIds: string[];
};

type WeeklyReminderState = {
  week: string;
  count: number;
};

function localDateKey(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function weekKey(date: Date): string {
  const start = new Date(date.getFullYear(), 0, 1);
  const dayNumber = Math.floor((date.getTime() - start.getTime()) / 86_400_000) + 1;
  const weekNumber = Math.ceil((dayNumber + start.getDay()) / 7);
  return `${date.getFullYear()}-${String(weekNumber).padStart(2, '0')}`;
}

function readShownState(todayKey: string): ShownReminderState {
  try {
    const raw = localStorage.getItem(REMINDERS_SHOWN_KEY);
    if (!raw) return { date: todayKey, taskIds: [] };
    const parsed = JSON.parse(raw) as Partial<ShownReminderState>;
    if (parsed.date !== todayKey) return { date: todayKey, taskIds: [] };
    return {
      date: todayKey,
      taskIds: Array.isArray(parsed.taskIds)
        ? parsed.taskIds.filter((id): id is string => typeof id === 'string')
        : [],
    };
  } catch {
    return { date: todayKey, taskIds: [] };
  }
}

function writeShownState(state: ShownReminderState): void {
  try { localStorage.setItem(REMINDERS_SHOWN_KEY, JSON.stringify(state)); } catch {}
}

function readWeeklyState(currentWeek: string): WeeklyReminderState {
  try {
    const raw = localStorage.getItem(REMINDERS_WEEKLY_KEY);
    if (!raw) return { week: currentWeek, count: 0 };
    const parsed = JSON.parse(raw) as Partial<WeeklyReminderState>;
    if (parsed.week !== currentWeek) return { week: currentWeek, count: 0 };
    return {
      week: currentWeek,
      count: typeof parsed.count === 'number' ? parsed.count : 0,
    };
  } catch {
    return { week: currentWeek, count: 0 };
  }
}

function writeWeeklyState(state: WeeklyReminderState): void {
  try { localStorage.setItem(REMINDERS_WEEKLY_KEY, JSON.stringify(state)); } catch {}
}

function readRemindersEnabled(): boolean {
  try {
    return localStorage.getItem(REMINDERS_ENABLED_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeRemindersEnabled(enabled: boolean): void {
  try { localStorage.setItem(REMINDERS_ENABLED_KEY, String(enabled)); } catch {}
}

function markReminderShown(taskId: string, today: Date, isPro: boolean): void {
  const todayKey = localDateKey(today);
  const shownState = readShownState(todayKey);
  const alreadyShown = shownState.taskIds.includes(taskId);

  if (!alreadyShown) {
    writeShownState({
      date: todayKey,
      taskIds: [...shownState.taskIds, taskId],
    });
  }

  if (!isPro && !alreadyShown) {
    const currentWeek = weekKey(today);
    const weeklyState = readWeeklyState(currentWeek);
    writeWeeklyState({
      week: currentWeek,
      count: weeklyState.count + 1,
    });
  }
}

export function useGardenReminders() {
  const { user, loading } = useAuth();
  const { zone, favorites, seedInventory, isLoaded } = useUserData();
  const [candidatePlants, setCandidatePlants] = useState<CanonicalPlant[]>([]);
  const [today] = useState(() => new Date());
  const [enabled, setEnabled] = useState(true);
  const [reminders, setReminders] = useState<GardenTask[]>([]);

  const isPro = user ? isProUser(user) : false;

  useEffect(() => {
    setEnabled(readRemindersEnabled());
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadCandidatePlants() {
      const candidateIds = new Set<string>();
      for (const id of favorites) candidateIds.add(id);
      for (const packet of seedInventory) {
        if (packet.plantId && packet.quantity !== 'empty') candidateIds.add(packet.plantId);
      }

      if (candidateIds.size === 0) {
        setCandidatePlants([]);
        return;
      }

      try {
        const plants = await resolveCanonicalPlantsById(candidateIds, {
          includeDraftGenerated: true,
        });
        if (!cancelled) setCandidatePlants(plants);
      } catch {
        if (!cancelled) setCandidatePlants([]);
      }
    }

    void loadCandidatePlants();
    return () => { cancelled = true; };
  }, [favorites, seedInventory]);

  useEffect(() => {
    if (loading || !isLoaded || !enabled || !zone || candidatePlants.length === 0) {
      setReminders([]);
      return;
    }

    const todayKey = localDateKey(today);
    const shownState = readShownState(todayKey);
    const shownIds = new Set(shownState.taskIds);
    const weeklyState = readWeeklyState(weekKey(today));

    if (!isPro && weeklyState.count >= 1) {
      setReminders([]);
      return;
    }

    const dueReminders = generateGardenTasks({
      zone,
      plants: candidatePlants,
      today,
    }).filter(task => task.shouldNotify && !shownIds.has(task.id));

    setReminders(isPro ? dueReminders : dueReminders.slice(0, 1));
  }, [candidatePlants, enabled, isLoaded, isPro, loading, today, zone]);

  const activeReminderId = reminders[0]?.id ?? null;

  useEffect(() => {
    if (!activeReminderId) return;
    markReminderShown(activeReminderId, today, isPro);
  }, [activeReminderId, isPro, today]);

  const dismissReminder = useCallback((taskId: string) => {
    setReminders(current => current.filter(task => task.id !== taskId));
  }, []);

  const disableReminders = useCallback(() => {
    writeRemindersEnabled(false);
    setEnabled(false);
    setReminders([]);
  }, []);

  return {
    reminders,
    remindersEnabled: enabled,
    dismissReminder,
    disableReminders,
  };
}
